import type { Editor } from "@smartsuite/automation-engine-api";
import type * as filestack from "filestack-js";
import { useRootStore } from "../../stores/hooks";
import { getFileType } from "./file-types";
import type { SharedFileInput } from "./files";
import { getFilestackClient, storeSharedFiles } from "./filestack-client";
import { acceptFileFn } from "./accept-file";

interface FileUpload {
  openPicker: () => Promise<void>;
}

// Mirrors `defaultSources` in the main frontend's Filestack picker; Filestack's own defaults omit OneDrive.
export const PICKER_SOURCES = [
  "local_file_system",
  "url",
  "webcam",
  "box",
  "dropbox",
  "googledrive",
  "onedrive",
  "onedriveforbusiness",
];

// Filestack's picker opens the Google Picker under Filestack's own Google Cloud project by
// default, but our Filestack app authorizes Google Drive through SmartSuite's Google OAuth
// app. Google grants drive.file access only to the project the picker was opened with, so
// under the default id every picked file fails to store. Same value as the main frontend.
export const GOOGLE_DRIVE_APP_ID = "271151917782";

// Same cap as the main frontend (fileStackMaxSize): the picker rejects oversized files up
// front with its own toast instead of Filestack failing the upload silently.
export const DEFAULT_MAX_UPLOAD_SIZE = 5_000_000_000;

export const useFileUpload = (
  onChange: (files: SharedFileInput[]) => void,
  editor?: Editor,
): FileUpload => {
  const { config } = useRootStore();

  const initPicker = async (): Promise<filestack.PickerInstance> => {
    if (editor?.editor.oneofKind !== "file") {
      throw Error(
        `Expected to get file editor but got ${String(
          editor?.editor.oneofKind,
        )}`,
      );
    }
    const filestack = await getFilestackClient(config);
    return filestack.picker({
      acceptFn: acceptFileFn,
      fromSources: PICKER_SOURCES,
      googleDriveAppID: GOOGLE_DRIVE_APP_ID,
      ...(editor.editor.file.maximumUploadFiles === undefined
        ? {}
        : { maxFiles: editor.editor.file.maximumUploadFiles }),
      maxSize:
        editor.editor.file.maximumUploadSize === undefined
          ? DEFAULT_MAX_UPLOAD_SIZE
          : editor.editor.file.maximumUploadSize * 1024 * 1024,
      onUploadDone: (response: filestack.PickerResponse): void => {
        void uploadHandler(response.filesUploaded);
      },
    });
  };

  const uploadHandler = async (
    filesUploaded: filestack.PickerFileMetadata[],
  ): Promise<void> => {
    const sharedFiles: SharedFileInput[] = filesUploaded.map((file) => ({
      handle: file.handle,
      metadata: {
        container: file.container ?? "",
        filename: file.filename,
        key: file.key ?? "",
        mimetype: file.mimetype ?? "unknown",
        size: file.size,
      },
      fileType: getFileType(file.mimetype),
    }));
    await storeSharedFiles(config, sharedFiles);
    onChange(sharedFiles);
  };

  const openPicker = async (): Promise<void> => {
    const picker = await initPicker();
    void picker.open();
  };

  return { openPicker };
};
